/**
 * ConstructionWorkerView Component
 * 
 * Main view component for construction workers to access their payslip system.
 * This component handles:
 * - Loading worker information from sessionStorage
 * - Managing navigation between payslip and settings pages
 * - Responsive layout (mobile vs desktop)
 * - Sidebar collapse/expand functionality
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import '../EmployeeView/EmployeeView.css';
import { ConstructionEmpPreview } from '../../layouts/PaySlipPreview/components/ConstructionEmp/ConstructionEmpPreview';
import { ConstructionWorkerSidebar } from './ConstructionWorkerSidebar';
import { ConstructionWorkerSettings } from './ConstructionWorkerSettings';
import { EmployeeDocuments } from '../../layouts/DocumentDelivery/EmployeeDocuments';
import ConstructionWorkerService from '../../services/ConstructionWorkerService';
import { getConsentFormConfig, getConsentStatus, submitConsent } from '../../services/ConsentService';
import { ConsentModal } from '../FirstLoginConsent/ConsentModal';
import type { ResolvedConsentFormConfig } from '../FirstLoginConsent/consentFormTypes';
import { buildLocalFallbackConsentConfig } from '../FirstLoginConsent/consentDefaults';

/**
 * Interface defining the structure of a construction worker object
 */
interface ConstructionWorker {
  empCode: string;
  name: string;
  userType: string;
}

export const ConstructionWorkerView: React.FC = () => {
  // State for storing the currently logged-in construction worker
  const [currentWorker, setCurrentWorker] = useState<ConstructionWorker | null>(null);
  
  // Loading state to show spinner while fetching worker data
  const [isLoading, setIsLoading] = useState(true);
  
  // Current active page ('payslip' or 'settings')
  const [activePage, setActivePage] = useState('payslip');
  
  // State to control sidebar collapse/expand behavior
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  // Flag to determine if the current viewport is mobile (width <= 768px)
  const [isMobile, setIsMobile] = useState(false);
  // First-time consent modal: show when user has not yet submitted consent (backend)
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentCheckDone, setConsentCheckDone] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [consentFormConfig, setConsentFormConfig] = useState<ResolvedConsentFormConfig | null>(null);

  /**
   * Effect hook to load construction worker data from sessionStorage on component mount.
   * The worker data is stored under the 'currentEmployee' key after login.
   */
  useEffect(() => {
    // Get current construction worker from sessionStorage
    const storedWorker = sessionStorage.getItem('currentEmployee');
    if (storedWorker) {
      setCurrentWorker(JSON.parse(storedWorker));
    }
    setIsLoading(false);
  }, []);

  /**
   * Effect: fetch consent status from backend; show modal only when user has not yet consented (first login).
   * Only show modal when API succeeds and hasConsented is false. On API failure, do not show modal.
   */
  useEffect(() => {
    if (!currentWorker) return;
    let cancelled = false;
    setConsentCheckDone(false);
    getConsentStatus(currentWorker.empCode, 'construction')
      .then((data) => {
        if (!cancelled) {
          setShowConsentModal(Boolean(data.hasConsented === false));
          setConsentCheckDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setShowConsentModal(false);
          setConsentCheckDone(true);
        }
      });
    return () => { cancelled = true; };
  }, [currentWorker]);

  useEffect(() => {
    if (!currentWorker) return;
    let cancelled = false;
    getConsentFormConfig()
      .then((c) => {
        if (cancelled) return;
        if (!c) {
          setConsentFormConfig(buildLocalFallbackConsentConfig());
          return;
        }
        setConsentFormConfig({
          sections: c.sections,
          printNoticeLines:
            c.printNoticeLines.length >= 2 ? [c.printNoticeLines[0], c.printNoticeLines[1]] : buildLocalFallbackConsentConfig().printNoticeLines,
        });
      })
      .catch(() => {
        if (!cancelled) setConsentFormConfig(buildLocalFallbackConsentConfig());
      });
    return () => {
      cancelled = true;
    };
  }, [currentWorker]);

  /**
   * Effect hook to detect mobile viewport and update state accordingly.
   * Also sets up a resize listener to handle dynamic window resizing.
   * Mobile breakpoint is set at 768px width.
   */
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };
    
    // Check on initial mount
    checkMobile();
    
    // Listen for window resize events
    window.addEventListener('resize', checkMobile);
    
    // Cleanup: remove event listener on unmount
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Handler for navigation page changes.
   * Updates the active page state to switch between payslip and settings views.
   * 
   * @param page - The page identifier ('payslip' or 'settings')
   */
  const handlePageChange = (page: string) => {
    setActivePage(page);
    // Don't automatically collapse sidebar on mobile - let user control it
  };

  /**
   * Toggles the sidebar collapse/expand state.
   * This affects the sidebar width and main content margin on desktop layouts.
   */
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  /**
   * Logout handler that clears worker data from sessionStorage and reloads the page.
   * This will return the user to the login screen.
   */
  const handleLogout = () => {
    ConstructionWorkerService.logout();
    window.location.reload(); // Reload to show login
  };

  const handleConsentAgree = async () => {
    if (!currentWorker || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: currentWorker.empCode,
        name: currentWorker.name,
        empType: 'construction',
        status: 'agree',
      });
      if (ok.success) setShowConsentModal(false);
    } finally {
      setConsentSubmitting(false);
    }
  };

  const handleConsentDisagree = async () => {
    if (!currentWorker || consentSubmitting) return;
    setConsentSubmitting(true);
    try {
      const ok = await submitConsent({
        empCode: currentWorker.empCode,
        name: currentWorker.name,
        empType: 'construction',
        status: 'disagree',
      });
      if (ok.success) {
        ConstructionWorkerService.logout();
        window.location.href = '/login';
      }
    } finally {
      setConsentSubmitting(false);
    }
  };

  /**
   * Renders the main content area based on the currently active page.
   * Returns either the payslip preview component or the settings component.
   * 
   * @returns JSX element for the active page content
   */
  const renderMainContent = () => {
    switch (activePage) {
      case 'payslip':
        // Render payslip preview page with worker's payslip information
        return (
          <div className="tab-container">
            <div className="tab-header">
              <h3>給与明細</h3>
              <p>あなたの給与明細を確認できます</p>
            </div>
            
            <div className="tab-content">
              <ConstructionEmpPreview 
                isEmployeeView={true}
                currentEmployeeCode={currentWorker!.empCode}
              />
            </div>
          </div>
        );
      case 'documents':
        return <EmployeeDocuments />;
      case 'settings':
        // Render settings page for account management and password changes
        return <ConstructionWorkerSettings currentWorker={currentWorker!} />;
      default:
        // Default to payslip view if unknown page is selected
        return (
          <div className="tab-container">
            <div className="tab-header">
              <h3>給与明細</h3>
              <p>あなたの給与明細を確認できます</p>
            </div>
            
            <div className="tab-content">
              <ConstructionEmpPreview 
                isEmployeeView={true}
                currentEmployeeCode={currentWorker!.empCode}
              />
            </div>
          </div>
        );
    }
  };

  // Show loading spinner while worker data is being fetched
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>読み込み中...</p>
      </div>
    );
  }

  // Error state: Show error message if worker data is not found in sessionStorage
  // This can happen if the tab was closed or user hasn't logged in
  if (!currentWorker) {
    return (
      <div className="error-container">
        <h2>エラー</h2>
        <p>建設作業員情報が見つかりません。再度ログインしてください。</p>
        <button onClick={handleLogout} className="logout-button">
          ログイン画面に戻る
        </button>
      </div>
    );
  }

  const resolvedConsentConfig = consentFormConfig ?? buildLocalFallbackConsentConfig();

  /**
   * Mobile Layout Configuration
   * Mobile layout has a vertical stack:
   * - Header at the top
   * - Collapsible sidebar below header
   * - Main content area at the bottom
   */
  if (isMobile) {
    return (
      <div className="employee-layout mobile-layout">
        {showConsentModal && (
          <ConsentModal
            empCode={currentWorker.empCode}
            name={currentWorker.name}
            config={resolvedConsentConfig}
            onAgree={handleConsentAgree}
            onDisagree={handleConsentDisagree}
            submitting={consentSubmitting}
          />
        )}
        {/* Header at the top for mobile layout */}
        <div className="employee-header">
          <div className="header-content">
            <div className="employee-info">
              <h2>給与明細システム</h2>
              <p>ようこそ、{currentWorker.name}さん</p>
              <p className="employee-code">従業員コード: {currentWorker.empCode}</p>
            </div>
            <button onClick={handleLogout} className="logout-button">
              🚪 ログアウト
            </button>
          </div>
        </div>
        
        {/* Collapsible sidebar below header */}
        <ConstructionWorkerSidebar 
          activePage={activePage} 
          setActivePage={handlePageChange} 
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          isMobile={isMobile}
        />
        
        {/* Main content area below sidebar */}
        <div className="mobile-content-area">
          {renderMainContent()}
        </div>
      </div>
    );
  }

  /**
   * Desktop Layout Configuration
   * Desktop layout has a horizontal arrangement:
   * - Fixed sidebar on the left (can be collapsed)
   * - Main content area on the right with dynamic margin based on sidebar state
   *   - Collapsed sidebar: 72px margin
   *   - Expanded sidebar: 282px margin
   */
  return (
    <div className="employee-layout">
      {showConsentModal && (
        <ConsentModal
          empCode={currentWorker.empCode}
          name={currentWorker.name}
          config={resolvedConsentConfig}
          onAgree={handleConsentAgree}
          onDisagree={handleConsentDisagree}
          submitting={consentSubmitting}
        />
      )}
      {/* Fixed sidebar on the left side */}
      <ConstructionWorkerSidebar 
        activePage={activePage} 
        setActivePage={handlePageChange} 
        isCollapsed={isSidebarCollapsed}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
      />
      
      {/* Main container that adjusts based on sidebar state */}
      {/* Margin dynamically changes when sidebar is collapsed/expanded for smooth transition */}
      <div 
        className={`main-container ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}
        style={{ 
          marginLeft: isSidebarCollapsed ? '72px' : '282px',
          transition: 'margin-left 0.3s ease'
        }}
      >
        {/* Header at the top of the main content area */}
        <div className="employee-header">
          <div className="header-content">
            <div className="employee-info">
              <h2>給与明細システム</h2>
              <p>ようこそ、{currentWorker.name}さん</p>
              <p className="employee-code">従業員コード: {currentWorker.empCode}</p>
            </div>
            <button onClick={handleLogout} className="logout-button">
              🚪 ログアウト
            </button>
          </div>
        </div>
        
        {/* Main content area below header */}
        <div className="content-area">
          {renderMainContent()}
        </div>
      </div>
    </div>
  );
};
